import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    Container,
    Skeleton,
    Chip
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import BogoCard from "../BogoCard/BogoCard";
import ScrollRow from "../ScrollRow/ScrollRow";
import { getUser } from "../../Utils/authHelper";
import SectionHeading from "../SectionHeading/SectionHeading";

const rubikfont = { fontFamily: "Rubik, sans-serif" };
const API_URL = "https://api.glamcompany.in/api/v1/";

const BogoSection = () => {
    const [bogos, setBogos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedGroupKey, setSelectedGroupKey] = useState("all");
    const navigate = useNavigate();

    const user = getUser();
    const isBusiness = user?.customerType === "business";

    useEffect(() => {
        const fetchBogos = async () => {
            try {
                const res = await fetch(`${API_URL}bogo-offer/all`);
                const data = await res.json();
                if (data.success) {
                    const rawList = data.data || [];
                    // Filter based on user customerType visibility
                    const filtered = rawList.filter(item => {
                        if (!item.visibility || item.visibility.length === 0) return true;
                        if (isBusiness) return item.visibility.includes("b2b");
                        return item.visibility.includes("b2c");
                    });
                    setBogos(filtered);
                }
            } catch (e) {
                console.error("Failed to fetch BOGO offers:", e);
            } finally {
                setLoading(false);
            }
        };
        fetchBogos();
    }, [isBusiness]);

    // Group offers based on Buy X Get Y count
    const groups = useMemo(() => {
        const map = {};
        bogos.forEach((bogo) => {
            const buyQty = Number(bogo.buyQuantity) || 1;
            const getQty = Number(bogo.getQuantity) || 1;
            const key = `buy_${buyQty}_get_${getQty}`;

            if (!map[key]) {
                map[key] = {
                    key,
                    buyQuantity: buyQty,
                    getQuantity: getQty,
                    title: `Buy ${buyQty} Get ${getQty} Free`,
                    shortTitle: `Buy ${buyQty} Get ${getQty}`,
                    items: []
                };
            }
            map[key].items.push(bogo);
        });

        // Sort groups by buyQuantity ascending, then getQuantity ascending
        return Object.values(map).sort((a, b) => {
            if (a.buyQuantity !== b.buyQuantity) {
                return a.buyQuantity - b.buyQuantity;
            }
            return a.getQuantity - b.getQuantity;
        });
    }, [bogos]);

    if (!loading && bogos.length === 0) return null;

    const displayedGroups = selectedGroupKey === "all"
        ? groups
        : groups.filter(g => g.key === selectedGroupKey);

    return (
        <Box sx={{ py: { xs: 2.5, md: 3.5 }, bgcolor: "#fff", position: "relative" }}>
            <Container maxWidth="xl">
                {/* Section Header */}
                <SectionHeading kicker="Exclusive Offers" title="Special Deals" onAction={() => navigate("/bogo-deals")} />

                {/* Group filter pills (when more than 1 distinct group exists) */}
                {!loading && groups.length > 1 && (
                    <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 1, mb: 2, scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
                        {[{ key: "all", title: "All deals", count: bogos.length }, ...groups.map((g) => ({ key: g.key, title: g.title, count: g.items.length }))].map((p) => {
                            const on = selectedGroupKey === p.key;
                            return (
                                <Box
                                    key={p.key}
                                    onClick={() => setSelectedGroupKey(p.key)}
                                    sx={{
                                        flexShrink: 0,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        px: 1.5,
                                        py: 0.6,
                                        borderRadius: "999px",
                                        cursor: "pointer",
                                        whiteSpace: "nowrap",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        border: on ? "1px solid #111" : "1px solid #e8dcc2",
                                        bgcolor: on ? "#111" : "#fff",
                                        color: on ? "#fff" : "#444",
                                        transition: "all .2s",
                                        "&:hover": { borderColor: "#C8A054", color: on ? "#fff" : "#C8A054" },
                                        ...rubikfont,
                                    }}
                                >
                                    {p.title}
                                    <Box component="span" sx={{ px: 0.75, borderRadius: "999px", fontSize: "10.5px", fontWeight: 700, bgcolor: on ? "rgba(255,255,255,0.2)" : "#f6efe0", color: on ? "#fff" : "#a8832f" }}>
                                        {p.count}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {/* Loading skeleton */}
                {loading ? (
                    <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2, md: 2.5 }, overflowX: "auto", pb: 2, scrollbarWidth: "none" }}>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Box key={i} sx={{ width: { xs: 160, sm: 190, md: 210 }, flexShrink: 0 }}>
                                <Skeleton variant="rectangular" sx={{ aspectRatio: "1/1", height: "auto", borderRadius: "12px", mb: 1.5 }} />
                                <Skeleton width="70%" height={20} sx={{ mb: 0.5 }} />
                                <Skeleton width="50%" height={20} />
                            </Box>
                        ))}
                    </Box>
                ) : (
                    /* Render by groups matching layout grids */
                    displayedGroups.map((group) => (
                        <Box key={group.key} sx={{ mb: 4 }}>
                            {/* Group Sub-heading with count badge */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                                <Typography sx={{ fontSize: { xs: "16px", md: "18px" }, fontWeight: 600, color: "#111", textTransform: "capitalize", ...rubikfont }}>
                                    {group.title}
                                </Typography>
                                <Chip
                                    label={`${group.items.length} ${group.items.length === 1 ? 'Offer' : 'Offers'}`}
                                    size="small"
                                    sx={{
                                        height: "20px",
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        bgcolor: "#fdf8ee",
                                        color: "#C8A054",
                                        border: "1px solid rgba(200,160,84,0.3)",
                                        ...rubikfont
                                    }}
                                />
                            </Box>

                            {/* Horizontal scroll container matching Layouts */}
                            <ScrollRow gap={{ xs: 1.5, sm: 2, md: 2.5 }}>
                                {group.items.map((bogo) => (
                                    <BogoCard key={bogo._id} bogo={bogo} width={{ xs: 160, sm: 190, md: 210 }} scrollTop />
                                ))}
                            </ScrollRow>
                        </Box>
                    ))
                )}
            </Container>
        </Box>
    );
};

export default BogoSection;


